import React, { useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const ImageTable = ({ images = [], onEdit, onDelete }) => {
  const [dimensions, setDimensions] = useState({}); // { id: { width, height } }

  useEffect(() => {
    if (!images?.length) return;

    const loadDimensions = async () => {
      const updatedDims = {};
      await Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (!img?.imageLink) return resolve();

              const imageObj = new Image();
              imageObj.src = img.imageLink;

              imageObj.onload = () => {
                updatedDims[img._id] = {
                  width: imageObj.width,
                  height: imageObj.height,
                };
                resolve();
              };

              imageObj.onerror = resolve;
            })
        )
      );
      setDimensions(updatedDims);
    };

    loadDimensions();
  }, [images]);

  return (
    <div className="bg-white overflow-hidden transition-all duration-300">
      {/* Header */}
      <div className="px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-neutral-50 to-neutral-100 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-neutral-800 tracking-wide">
          Gallery Images
        </h3>
        <span className="text-sm text-neutral-500">Total: {images.length || 0}</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left text-neutral-700">
          <thead className="bg-neutral-100 text-neutral-900 font-semibold text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-3">Preview</th>
              <th className="px-6 py-3">Image Size</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Order</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {images?.length > 0 ? (
              images.map((img, index) => {
                const dim = dimensions[img._id] || {};
                return (
                  <tr
                    key={index}
                    className="border-b border-neutral-200 hover:bg-neutral-50 transition-colors duration-200"
                  >
                    {/* Image */}
                    <td className="px-6 py-3">
                      <div className="flex items-center space-x-3">
                        <img
                          src={img?.imageLink}
                          alt={img?.title || "Gallery Image"}
                          className="h-14 w-24 object-cover rounded-lg border border-neutral-300 shadow-sm"
                        />
                        <div>
                          <p className="text-xs text-neutral-500">
                            ID: {img?._id?.slice(-6) || "N/A"}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Dimensions */}
                    <td className="px-6 py-3 text-neutral-700">
                      {dim.width && dim.height ? (
                        <span className="text-sm text-neutral-700">
                          {dim.width} × {dim.height}px
                        </span>
                      ) : (
                        <span className="text-neutral-400 text-xs">Loading...</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          img?.isFeatured
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {img?.isFeatured ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Order */}
                    <td className="px-6 py-3 text-neutral-700 font-medium">
                      {img?.order ?? "-"}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-3 text-right space-x-2">
                      <button
                        onClick={() => onEdit(img?._id)}
                        className="inline-flex items-center justify-center p-2 rounded-md text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </button>
                      <button
                        onClick={() => onDelete(img?._id)}
                        className="inline-flex items-center justify-center p-2 rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200"
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-8 text-center text-neutral-500 text-sm"
                >
                  No images found in the gallery.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImageTable;
